/**
 * The operator audit log page, minus the JSX (work order item 10, AC-10.1 / AC-10.2).
 *
 * JSX-free on purpose, like the rest of `src/lib`: the dashboard's Jest runs in `node` with no
 * jsdom, so the filter → query mapping, the shop link and the payload rendering are only testable
 * out here.
 *
 * **Read-only.** The rows are Verfahrensdokumentation; nothing on this page edits or deletes one,
 * and the API has no route that could (`operator-reason.totality.spec.ts` pins that).
 *
 * **Payloads load one at a time.** The list carries no payload — a payload can hold an end
 * customer's address or a broadcast's copy — and opening one is itself an audited read
 * (`audit.entry.view`), so it happens when the operator opens a row, never on hover or prefetch.
 */

import type { AuditLogFilters } from './ops-api';
import { SHOP_FILE_BASE, shopFilePath } from './shop-file';

/** Rows per page. The API caps at 100. */
export const AUDIT_PAGE_SIZE = 50;

/** `OperatorAuditLog.shopDomain` of an action that concerns no single shop. */
export const CROSS_SHOP_DOMAIN = '*';

/** Same shape rule as the API's `OperatorAuditQueryDto.shopDomain`. */
const SHOP_DOMAIN_FILTER = /^(\*|[a-z0-9][a-z0-9-]*\.myshopify\.com)$/;

/** The filter form as the operator fills it — every field a string, `''` meaning "any". */
export interface AuditFilterForm {
  readonly operator: string;
  readonly action: string;
  readonly shopDomain: string;
  /** `YYYY-MM-DD` from a date input, the operator's local day. */
  readonly from: string;
  /** `YYYY-MM-DD`, inclusive. */
  readonly to: string;
}

/** The empty form. */
export const EMPTY_AUDIT_FILTERS: AuditFilterForm = {
  operator: '',
  action: '',
  shopDomain: '',
  from: '',
  to: '',
};

/**
 * Whether the shop filter is one the API will accept. `''` (no filter) and `*` (cross-shop
 * actions) are valid; anything else must be a full myshopify domain, because the filter is an
 * equality seek on the `(shopDomain, createdAt)` index, not a search.
 */
export function isShopDomainFilterValid(value: string): boolean {
  const trimmed = value.trim().toLowerCase();
  return trimmed === '' || SHOP_DOMAIN_FILTER.test(trimmed);
}

/** Whether the date range is usable: both ends optional, but not reversed. */
export function isDateRangeValid(from: string, to: string): boolean {
  return !from || !to || from <= to;
}

/**
 * Midnight at the start of a local calendar day, as ISO. The operator picks days in their own
 * timezone; the API compares instants.
 */
function startOfLocalDay(day: string): string | undefined {
  const date = new Date(`${day}T00:00:00`);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

/** The last millisecond of a local calendar day, so `to` includes the day it names. */
function endOfLocalDay(day: string): string | undefined {
  const date = new Date(`${day}T23:59:59.999`);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

/**
 * The API query for a filter form and a page. Empty fields are omitted rather than sent as `''`,
 * which the API's validators would read as a value.
 */
export function auditQuery(
  form: AuditFilterForm,
  page: number,
): AuditLogFilters {
  const operator = form.operator.trim();
  const action = form.action.trim();
  const shopDomain = form.shopDomain.trim().toLowerCase();
  const from = form.from ? startOfLocalDay(form.from) : undefined;
  const to = form.to ? endOfLocalDay(form.to) : undefined;
  return {
    ...(operator ? { operator } : {}),
    ...(action ? { action } : {}),
    ...(shopDomain ? { shopDomain } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
    page: Math.max(1, page),
    limit: AUDIT_PAGE_SIZE,
  };
}

/** Number of pages for a total, never below one. */
export function auditPageCount(total: number, limit: number): number {
  return Math.max(1, Math.ceil(total / Math.max(1, limit)));
}

/**
 * Where a row's shop links to, or null for a cross-shop row.
 *
 * The shop file when the row carries the shop's id; otherwise the shop search for the domain —
 * a row keeps its domain after the shop is deleted and its `shopId` is set null, and older rows
 * never had one.
 */
export function auditShopLink(row: {
  readonly shopId?: string | null;
  readonly shopDomain: string;
}): string | null {
  if (row.shopDomain === CROSS_SHOP_DOMAIN) return null;
  if (row.shopId) return shopFilePath(row.shopId);
  return `${SHOP_FILE_BASE}?${new URLSearchParams({ q: row.shopDomain }).toString()}`;
}

/**
 * The catalogue key of an action token's friendly label.
 *
 * BUILT, not listed: the tokens are dotted lower-case strings that look exactly like catalogue
 * keys, so quoting them here would trip the parity spec's key scan. The page shows the raw token
 * always and the label only when the catalogue has one — a token a future tool starts writing
 * renders as itself, not as a dotted path. `audit-log.spec.ts` holds the list of known tokens and
 * checks each has copy.
 */
export function auditActionLabelKey(action: string): string {
  return `auditLog.actionLabels.${action.replace(/[^A-Za-z0-9]+/g, '_')}`;
}

/** The outcome an up-front row was completed with, when the payload carries one. */
export type AuditOutcome = 'pending' | 'succeeded' | 'failed';

/**
 * The outcome stamped into a payload by `OperatorAuditService.runAudited` / the backfill, or null
 * for a row that records a single moment (a view, an open).
 *
 * A `pending` that stays pending is itself a finding: the action started and its completion was
 * never written — the process died, or the follow-up write failed.
 */
export function auditOutcome(
  payload: Record<string, unknown>,
): AuditOutcome | null {
  const status = payload['status'];
  return status === 'pending' || status === 'succeeded' || status === 'failed'
    ? status
    : null;
}

/**
 * A payload as indented JSON (AC-10.2: unknown shapes render, never fail).
 *
 * Shapes differ per action and per version; the API already wraps a non-object as `{ value }`.
 * This only has to never throw — a value `JSON.stringify` cannot take falls back to `String`.
 */
export function formatAuditPayload(payload: unknown): string {
  try {
    return JSON.stringify(payload, null, 2) ?? String(payload);
  } catch {
    return String(payload);
  }
}

/**
 * URL parameter names of the page. The filters live in the URL so a view can be linked to a
 * colleague ("what did we do on this shop last week") and so a write's success banner can link
 * straight to the row it recorded (`?entry=`).
 */
const PARAM = {
  operator: 'operator',
  action: 'action',
  shopDomain: 'shop',
  from: 'from',
  to: 'to',
  page: 'page',
  entry: 'entry',
} as const;

/** What the page reads off its URL. */
export interface AuditUrlState {
  readonly form: AuditFilterForm;
  readonly page: number;
  /** The entry whose drawer is open, if any. */
  readonly entry: string | null;
}

/** Reads the page state off its search params; anything missing or malformed is "any". */
export function auditStateFromParams(params: URLSearchParams): AuditUrlState {
  const read = (name: string) => params.get(name)?.trim() ?? '';
  const day = (name: string) => {
    const value = read(name);
    return /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : '';
  };
  const page = Number.parseInt(read(PARAM.page), 10);
  return {
    form: {
      operator: read(PARAM.operator),
      action: read(PARAM.action),
      shopDomain: read(PARAM.shopDomain),
      from: day(PARAM.from),
      to: day(PARAM.to),
    },
    page: Number.isFinite(page) && page > 0 ? page : 1,
    entry: read(PARAM.entry) || null,
  };
}

/** Writes page state back to search params, omitting empty values and page 1. */
export function auditParamsFromState(state: AuditUrlState): URLSearchParams {
  const params = new URLSearchParams();
  const set = (name: string, value: string) => {
    const trimmed = value.trim();
    if (trimmed) params.set(name, trimmed);
  };
  set(PARAM.operator, state.form.operator);
  set(PARAM.action, state.form.action);
  set(PARAM.shopDomain, state.form.shopDomain.toLowerCase());
  set(PARAM.from, state.form.from);
  set(PARAM.to, state.form.to);
  if (state.page > 1) params.set(PARAM.page, String(state.page));
  if (state.entry) params.set(PARAM.entry, state.entry);
  return params;
}

/**
 * A dropdown's options: what the log contains, plus the current value when the log does not
 * offer it (a linked URL naming an operator who has since left) — otherwise the select would
 * silently show "any" while the list is filtered.
 */
export function auditFilterOptions(
  values: readonly string[],
  current: string,
): string[] {
  const trimmed = current.trim();
  return trimmed && !values.includes(trimmed)
    ? [trimmed, ...values]
    : [...values];
}
